import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { MessageCircle, RefreshCw, Send } from "lucide-react";
import { api, ApiError } from "../api";
import type { ChatMessage, ThreadDetail } from "../types";
import { Button, Spinner, TextAreaField } from "./ui";

export function useMessagingData<T>(url: string | null, interval = 5000) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const request = useRef<{ controller: AbortController; version: number } | null>(null);
  const generation = useRef(0);
  const refresh = useCallback(async () => {
    if (!url) return;
    request.current?.controller.abort();
    const controller = new AbortController(); const version = ++generation.current;
    request.current = { controller, version }; setLoading(true);
    const timer = window.setTimeout(() => controller.abort(), 12000);
    try {
      const result = await api<T>(url, { signal: controller.signal });
      if (version === generation.current) { setData(result); setError(""); }
    } catch (reason) {
      if (version === generation.current) setError(reason instanceof ApiError ? reason.message : "连接暂时中断，请重试。");
    } finally {
      window.clearTimeout(timer);
      if (version === generation.current) { request.current = null; setLoading(false); }
    }
  }, [url]);
  useEffect(() => {
    setData(null); setError("");
    if (!url) setLoading(false);
    void refresh();
    const tick = () => { if (!document.hidden && !request.current) void refresh(); };
    const timer = window.setInterval(tick, interval);
    document.addEventListener("visibilitychange", tick);
    return () => { generation.current += 1; request.current?.controller.abort(); request.current = null; window.clearInterval(timer); document.removeEventListener("visibilitychange", tick); };
  }, [refresh, interval, url]);
  return { data, error, loading, refresh };
}

const chatTime = new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Shanghai" });

export function ChatPanel({ role, threadId, onUpdate, onExpired, onDraftChange }: {
  role: "user" | "admin"; threadId?: string; onUpdate?: () => void; onExpired?: () => void; onDraftChange?: (dirty: boolean, busy: boolean) => void;
}) {
  const [limit, setLimit] = useState(50);
  const endpoint = role === "user" ? "/api/user/support" : `/api/admin/support/threads/${threadId}`;
  const { data, error, loading, refresh } = useMessagingData<ThreadDetail>(`${endpoint}?limit=${limit}`);
  const [draft, setDraft] = useState("");
  const [sendError, setSendError] = useState("");
  const [sending, setSending] = useState(false);
  const [readError, setReadError] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const pending = useRef<{ clientId: string; text: string } | null>(null);
  const lock = useRef(false);
  const mounted = useRef(true);
  const scroll = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const readSeq = useRef(0);
  const latestSeq = data?.messages.at(-1)?.seq ?? 0;

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { onDraftChange?.(Boolean(draft), sending); return () => onDraftChange?.(false, false); }, [draft, sending, onDraftChange]);
  useEffect(() => {
    if (!draft) return;
    const unload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", unload);
    return () => window.removeEventListener("beforeunload", unload);
  }, [draft]);
  useEffect(() => { if (atBottom.current && scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight; }, [latestSeq]);
  useEffect(() => {
    if (document.hidden || !latestSeq || readSeq.current >= latestSeq) return;
    let active = true;
    void api(`${endpoint}/read`, { method: "POST", signal: AbortSignal.timeout(12000), body: JSON.stringify({ seq: latestSeq }) })
      .then(() => { if (active) { readSeq.current = latestSeq; setReadError(false); onUpdate?.(); } })
      .catch(() => { if (active) setReadError(true); });
    return () => { active = false; };
  }, [data, endpoint, latestSeq, onUpdate]);

  async function send(event: FormEvent) {
    event.preventDefault(); if (lock.current || !draft.trim()) return;
    if (draft.trim().length > 2000) { setSendError("消息最多 2000 个字符。"); return; }
    pending.current ??= { clientId: crypto.randomUUID(), text: draft.trim() };
    lock.current = true; setSending(true); setSendError("");
    try {
      await api<ChatMessage>(`${endpoint}/messages`, { method: "POST", signal: AbortSignal.timeout(15000), body: JSON.stringify(pending.current) });
      if (!mounted.current) return;
      pending.current = null; setDraft(""); setRetrying(false); atBottom.current = true;
      await refresh(); onUpdate?.();
    } catch (reason) {
      if (!mounted.current) return;
      if (reason instanceof ApiError && reason.status < 500) {
        pending.current = null; setRetrying(false); setSendError(reason.message);
        if (reason.status === 401) onExpired?.();
      } else { setRetrying(true); setSendError("暂未确认发送结果，可重试同一条消息，不会重复发送。"); }
    } finally { lock.current = false; if (mounted.current) setSending(false); }
  }

  return <div className={`chat-panel chat-panel--${role}`}>
    <div className="chat-status"><span><i/>{data?.thread?.status === "resolved" ? "会话已结束，可发送新消息继续咨询" : role === "user" ? "星河客服 · 回复后会显示在这里" : "会员咨询"}</span><button type="button" aria-label="刷新会话" disabled={loading} onClick={() => void refresh()}><RefreshCw size={15} className={loading ? "mobile-spinning" : ""}/></button></div>
    {error && <div className="chat-error" role="alert">{error}<button onClick={() => void refresh()}>重试</button></div>}
    {readError && <p className="chat-read-warning" role="status">已读状态同步中</p>}
    <div className="chat-messages" ref={scroll} role="log" aria-label="客服会话消息" aria-live="polite" onScroll={() => { if (scroll.current) atBottom.current = scroll.current.scrollHeight - scroll.current.scrollTop - scroll.current.clientHeight < 70; }}>
      {data && data.total > data.messages.length && <button className="chat-load-more" disabled={loading} onClick={() => { atBottom.current = false; setLimit(value => value + 50); }}>查看更早消息</button>}
      {!data && loading ? <Spinner label="正在加载会话"/> : !data?.messages.length ? <div className="chat-empty"><MessageCircle/><strong>你好，有什么可以帮你？</strong><p>请描述遇到的问题，客服会在这里回复。</p></div> : data.messages.map(message => <article key={message.id} className={`chat-message ${message.sender === role ? "is-mine" : ""}`}><span>{message.sender === "admin" ? "星河客服" : "会员"} · {chatTime.format(new Date(message.createdAt))}</span><p>{message.text}</p>{message.sender === role && <small>{message.seq <= (role === "user" ? data.thread?.adminReadSeq ?? 0 : data.thread?.userReadSeq ?? 0) ? "已读" : "已发送"}</small>}</article>)}
    </div>
    <form className="chat-composer" noValidate onSubmit={send}>
      <TextAreaField label={role === "user" ? "咨询内容" : "回复内容"} id={`chat-draft-${role}`} value={draft} maxLength={2000} disabled={sending || retrying} placeholder={role === "user" ? "输入你的问题…" : "输入回复内容…"} onChange={event => { setDraft(event.target.value); setSendError(""); }} rows={2}/>
      {sendError && <p className="chat-error" role="alert">{sendError}</p>}
      <div><span>{draft.length}/2000</span><Button type="submit" busy={sending} disabled={!draft.trim()}><Send size={16}/>{retrying ? "重试发送" : "发送"}</Button></div>
    </form>
  </div>;
}
